import { Box } from '@wix/design-system';
import classNames from 'classnames';
import {
  ProductHatUi,
  useProductHat,
  useSelectButton,
  useEffectiveRecommendation,
  useRecommendationRevealEnabled,
} from '@wix/package-picker-shared-eclipse';
import { useCurrentPlanTab } from '../ProductCards/ProductCard/useCurrentPlanTab';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import React from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { EclipsePrice } from '../EclipsePrice/EclipsePrice';
import {
  MoneyBackHoverLabel,
  moneyBackHoverCtaClassName,
} from '../MoneyBackHoverLabel';
import { useMoneyBackCtaHover, useMoneyBackLabelViewed } from '../../hooks';
import s from './FeaturesGrid.scss';

interface FeaturesGridProductHeaderProps {
  productId: string;
}

export const FeaturesGridProductHeader = ({
  productId,
}: FeaturesGridProductHeaderProps) => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { data: product, isLoading } =
    EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchProduct(
      {
        productId,
      },
    );
  const { isEnabled: isRevealEnabled } = useRecommendationRevealEnabled();
  const { showsTab: showsCurrentPlanHat } = useCurrentPlanTab(productId);
  const { renderProductHat } = useProductHat({
    productId,
    excludeCurrentProductVariant: showsCurrentPlanHat,
  });

  const { SelectButton } = useSelectButton({ productId });
  const { isEnabled: showMoneyBackHover } = useMoneyBackCtaHover();
  const moneyBackLabelViewedProps = useMoneyBackLabelViewed(showMoneyBackHover);

  const { isEnterprise, name } = product ?? {};
  const { isRecommended } = useEffectiveRecommendation(
    productId,
    product?.isRecommended ?? false,
  );

  if (isLoading) {
    return null;
  }

  const backgroundGradient = isRecommended
    ? 'linear-gradient(180deg, #FFF 0%, #FAF9FF 100%)'
    : 'white';

  return (
    <PlatformFeaturesGrid.Header.Cell
      gap="SP1"
      background={backgroundGradient}
      border="none"
      marginTop="-28px"
      className={isRevealEnabled ? s.headerCellReveal : undefined}
    >
      <Box padding="0 SP1" boxSizing="border-box" position="relative">
        {showsCurrentPlanHat && (
          <div
            className={s.gridCurrentPlanHat}
            data-hook="grid-current-plan-hat"
          >
            <ProductHatUi
              variant="currentProduct"
              text={t('premium-purchase-plan.eclipse.cards.hat.current.plan')}
              height="26px"
            />
          </div>
        )}
        {renderProductHat({ height: '26px' })}
      </Box>
      <Box align="center" marginTop="SP2">
        <PlatformFeaturesGrid.Header.Title
          title={name ?? ''}
          size="small"
          className={s.gridPlanTitle}
          dataHook={`grid-plan-title-${productId}`}
        />
      </Box>
      <Box visibility={isEnterprise ? 'hidden' : 'visible'} align="center">
        <EclipsePrice productId={productId} />
      </Box>
      <Box
        align="center"
        width="100%"
        marginBottom={showMoneyBackHover ? 'SP2' : 'SP3'}
        marginTop="SP1"
        direction={showMoneyBackHover ? 'vertical' : undefined}
        gap={showMoneyBackHover ? 'SP1' : undefined}
        className={classNames({
          [moneyBackHoverCtaClassName]: showMoneyBackHover,
        })}
      >
        <Box
          align="center"
          width="126px"
          marginBottom={showMoneyBackHover ? undefined : 'SP3'}
          marginTop="SP1"
        >
          <SelectButton
            variant={isRecommended ? 'primary' : 'secondary'}
            size="medium"
            fullWidth
            {...moneyBackLabelViewedProps}
          />
        </Box>
        {showMoneyBackHover && <MoneyBackHoverLabel />}
      </Box>
    </PlatformFeaturesGrid.Header.Cell>
  );
};
